import { type IsinAmountLine, isValidIsinAmountLine, parseIsinAmountLines } from "@repo/shared";
import type { FinancialEntryItem, FinancialFieldDefinition } from "@repo/shared/types";
import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { lookupISINs } from "@/lib/api/openfigi";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./add-isin-modal.i18n";

type AddISINModalProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (entries: FinancialEntryItem[]) => void;
  assetFields: FinancialFieldDefinition[];
  /** How many more asset rows fit under the per-category limit. */
  capacity: number;
  disabled?: boolean;
};

export function AddISINModal({
  open,
  onClose,
  onAdd,
  assetFields,
  capacity,
  disabled,
}: AddISINModalProps) {
  const { formatNumber, regionalFormat } = useRegionalFormat();
  const t = useT(T);
  const [input, setInput] = useState("");
  // ISIN -> looked-up name (null when OpenFIGI has none). Kept across edits so
  // fixing an amount does not throw the names away.
  const [names, setNames] = useState<Record<string, string | null>>({});
  const [lookupFailed, setLookupFailed] = useState(false);
  // The dialog stays mounted between openings, so the lookup's busy flag is
  // our own state: closing clears it even while a request is still out.
  const [isPending, setIsPending] = useState(false);
  // Bumped on every lookup and on close, so a lookup answering after the
  // dialog closed (or after a newer lookup) is dropped.
  const lookupRun = useRef(0);

  const lines = useMemo(() => parseIsinAmountLines(input, regionalFormat), [input, regionalFormat]);
  const validLines = lines.filter(isValidIsinAmountLine);
  // The widget caps each category; valid lines past the remaining room are
  // shown as over the limit instead of being dropped silently on add.
  const addableLines = validLines.slice(0, Math.max(capacity, 0));
  const overLimitLines = new Set<IsinAmountLine>(validLines.slice(addableLines.length));

  // QL-294 — the lookup only adds names; rows are addable without it. Only the
  // rows that will be added are looked up, so an over-long paste cannot push
  // the request past OpenFIGI's per-request limit.
  const handleLookup = async () => {
    const isins = [...new Set(addableLines.map((line) => line.isin))];
    if (isins.length === 0) return;
    const run = ++lookupRun.current;
    setIsPending(true);

    try {
      const results = await lookupISINs(isins);
      if (run !== lookupRun.current) return;

      const found: Record<string, string | null> = {};
      const failed: { isin: string; error: string }[] = [];
      for (const result of results) {
        const isin = result.isin.toUpperCase();
        if (result.error) failed.push({ isin, error: result.error });
        else found[isin] = result.name || null;
      }
      if (failed.length > 0) {
        log.warn({ failed }, "isin name lookup failed for some isins");
      }
      setNames((prev) => ({ ...prev, ...found }));
      setLookupFailed(failed.length > 0);
    } catch (err) {
      if (run !== lookupRun.current) return;
      log.error({ err }, "isin name lookup failed");
      setLookupFailed(true);
    } finally {
      if (run === lookupRun.current) setIsPending(false);
    }
  };

  const handleSubmit = () => {
    // QL-39 — fields no longer carry a taxonomy type; the first asset field is
    // the default bucket and the advisor re-points the row if needed.
    const investmentType = assetFields[0];

    if (!investmentType) return;

    const newEntries: FinancialEntryItem[] = addableLines.map((line) => ({
      id: crypto.randomUUID(),
      typeId: investmentType.id,
      typeLabel: investmentType.label,
      label: names[line.isin] || line.isin,
      amount: line.amount,
      isin: line.isin,
    }));

    onAdd(newEntries);
    handleClose();
  };

  const handleClose = () => {
    lookupRun.current++;
    setIsPending(false);
    setInput("");
    setNames({});
    setLookupFailed(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogScrollContent className="max-w-2xl max-h-[80vh]">
        <DialogScrollHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogScrollHeader>

        <DialogBody className="space-y-4">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="NO0010096985,50000&#10;NO0010455694,120000&#10;US0378331005,75000"
            disabled={disabled || isPending}
            className="min-h-[200px] font-mono text-sm"
          />

          {lookupFailed && <p className="text-sm text-destructive">{t.lookupFailed}</p>}

          {/* Live preview of the parsed lines */}
          {lines.length > 0 && (
            <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
              <div className="text-sm font-medium text-muted-foreground">{t.preview}</div>
              {lines.map((line, index) => {
                const name = names[line.isin];
                return (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: lines have no identity; the same ISIN may repeat
                    key={index}
                    className="flex justify-between items-center text-sm py-1 border-b last:border-0"
                  >
                    <div className="flex-1">
                      <span className="font-mono text-xs">{line.isin}</span>
                      {line.error ? (
                        <span className="ml-2 text-destructive text-xs">
                          {t.lineErrors[line.error]}
                        </span>
                      ) : overLimitLines.has(line) ? (
                        <span className="ml-2 text-destructive text-xs">{t.overLimit}</span>
                      ) : name ? (
                        <span className="ml-2 text-muted-foreground">{name}</span>
                      ) : name === null ? (
                        <span className="ml-2 text-muted-foreground text-xs">{t.nameNotFound}</span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      {line.amount != null ? formatNumber(line.amount) : "-"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogBody>

        <DialogScrollFooter className="flex gap-2">
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
          <Button
            data-dialog-guard-ignore
            type="button"
            variant="secondary"
            onClick={handleLookup}
            disabled={disabled || addableLines.length === 0}
            loading={isPending}
          >
            {t.lookupNames}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || isPending || addableLines.length === 0}
          >
            {addableLines.length === 1
              ? t.addEntry(addableLines.length)
              : t.addEntries(addableLines.length)}
          </Button>
        </DialogScrollFooter>
      </DialogScrollContent>
    </Dialog>
  );
}
